import { useEffect, useMemo, useRef, useState } from "react";
import { Document, Page } from "react-pdf";
import { File } from "../../models/File";
import { Box, Button } from "@mui/material";
import { CheckIcon, FileXIcon, TrashIcon, XIcon } from "@phosphor-icons/react";
import { useFile } from "../../hooks/fileEditor/useFile";

type PdfWithDrawingProps = {
  file: File;
  extraPages: number;
  canvasRefs: React.MutableRefObject<Record<number, HTMLCanvasElement | null>>;
  previewCanvasRefs: React.MutableRefObject<
    Record<number, HTMLCanvasElement | null>
  >;
  removedPages: Set<number>;
  onLoad: (numpages: number) => void;
  onPointerDown: (e: React.PointerEvent<HTMLCanvasElement>) => void;
  onPointerMove: (
    e: React.PointerEvent<HTMLCanvasElement>,
    page: number,
  ) => void;
  onPointerUp: (e: React.PointerEvent<HTMLCanvasElement>) => void;
  onPageRemove: (pageIndex: number) => void;
  onCanvasReady?: (pageIndex: number, canvas: HTMLCanvasElement | null) => void;
};

const dpr = window.devicePixelRatio || 1;

export function PageLoader({
  file,
  extraPages,
  canvasRefs,
  previewCanvasRefs,
  removedPages,
  onPointerDown,
  onPointerMove,
  onPointerUp,
  onPageRemove,
  onLoad,
  onCanvasReady
}: PdfWithDrawingProps) {
  const [numPages, setNumPages] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [scale, setScale] = useState(1);
  const [confirmation, setConfirmation] = useState<Record<number, boolean>>({});
  const pdfViewportWidthRef = useRef<number | null>(null);
  const pdfViewportHeightRef = useRef<number | null>(null);

  const initializedCanvases = useRef<Map<number, { w: number; h: number }>>(
    new Map(),
  );

  const totalPages = numPages + extraPages;

  useEffect(() => {
    pdfViewportWidthRef.current = null;
    pdfViewportHeightRef.current = null;
    setScale(1);
  }, [file.base64]);

  useEffect(() => {
    if (!containerRef.current) return;

    const el = containerRef.current;

    const measure = () => {
      const w = el.getBoundingClientRect().width;
      if (w > 0) setContainerWidth(w);
    };

    measure();

    const ro = new ResizeObserver(measure);
    ro.observe(el);

    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    if (!pdfViewportWidthRef.current || !containerWidth) return;
    setScale(containerWidth / pdfViewportWidthRef.current);
  }, [containerWidth]);

  useEffect(() => {
    setConfirmation((prev) => {
      const next = { ...prev };

      for (let i = 0; i < totalPages; i++) {
        if (!(i in next)) {
          next[i] = false;
        }
      }

      return next;
    });
  }, [totalPages]);

  const base64ToUint8Array = (base64: string) => {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
  };

  const pdfFile = useMemo(() => {
    return { data: base64ToUint8Array(file.base64) };
  }, [file.base64]);

  return (
    <Box ref={containerRef}>
      <Document
        file={pdfFile}
        onLoadSuccess={({ numPages }) => {
          onLoad(numPages);
          setNumPages(numPages);
        }}
        className="pdfDocument">
        {Array.from({ length: totalPages }, (_, i) => {
          if (removedPages.has(i)) return;
          const pageIndex = i;
          const pageNumber = i + 1;

          const vw = pdfViewportWidthRef.current;
          const vh = pdfViewportHeightRef.current;

          const cssWidth = vw ? vw * scale : 0;
          const cssHeight = vh ? vh * scale : 0;

          const setupExtraCanvas = (c: HTMLCanvasElement, page: number) => {
            if (!vw || !vh) return;

            const nextWidth = Math.round(cssWidth * dpr);
            const nextHeight = Math.round(cssHeight * dpr);

            const prev = initializedCanvases.current.get(page);

            if (prev && prev.w === nextWidth && prev.h === nextHeight) {
              return;
            }

            initializedCanvases.current.set(page, {
              w: nextWidth,
              h: nextHeight,
            });

            c.style.width = `${cssWidth}px`;
            c.style.height = `${cssHeight}px`;
            c.width = nextWidth;
            c.height = nextHeight;

            const ctx = c.getContext("2d");
            if (!ctx) return;

            ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
            ctx.imageSmoothingEnabled = false;
          };
          return (
            <Box key={pageNumber} sx={{ position: "relative" }}>
              {pageNumber <= numPages ? (
                <Page
                  pageNumber={pageNumber}
                  scale={scale}
                  renderTextLayer={false}
                  renderAnnotationLayer={false}
                  onRenderSuccess={(page) => {
                    const viewport = page.getViewport({ scale: 1 });

                    if (!pdfViewportWidthRef.current)
                      pdfViewportWidthRef.current = viewport.width;
                    if (!pdfViewportHeightRef.current)
                      pdfViewportHeightRef.current = viewport.height;

                    if (pageNumber === 1 && containerWidth > 0) {
                      const nextScale = containerWidth / viewport.width;
                      setScale((prev) =>
                        Math.abs(prev - nextScale) > 0.001 ? nextScale : prev,
                      );
                    }

                    const setup = (c?: HTMLCanvasElement | null) => {
                      if (!c || !vw || !vh) return;

                      const nextCssWidth = `${cssWidth}px`;
                      const nextCssHeight = `${cssHeight}px`;
                      const nextWidth = Math.round(cssWidth * dpr);
                      const nextHeight = Math.round(cssHeight * dpr);

                      if (
                        c.width === nextWidth &&
                        c.height === nextHeight &&
                        c.style.width === nextCssWidth &&
                        c.style.height === nextCssHeight
                      ) {
                        return;
                      }

                      c.style.width = nextCssWidth;
                      c.style.height = nextCssHeight;

                      c.width = nextWidth;
                      c.height = nextHeight;

                      const ctx = c.getContext("2d");
                      if (!ctx) return;

                      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
                      ctx.imageSmoothingEnabled = false;
                    };

                    setup(canvasRefs.current[pageIndex]);
                    setup(previewCanvasRefs.current[pageIndex]);
                  }}
                />
              ) : (
                <Box
                  sx={{
                    width: cssWidth || "100%",
                    height: cssHeight || 600,
                    backgroundColor: "#fff",
                  }}
                />
              )}
              <canvas
                className="drawCanvas"
                data-page={pageIndex}
                ref={(el) => {
                  canvasRefs.current[pageIndex] = el;

                  onCanvasReady?.(pageIndex, el);
                }}
                onPointerDown={onPointerDown}
                onPointerMove={(e) => onPointerMove(e, pageIndex)}
                onPointerUp={onPointerUp}
              />

              <canvas
                className="previewCanvas"
                data-page={pageIndex}
                ref={(el) => {
                  if (!el) return;
                  previewCanvasRefs.current[pageIndex] = el;

                  if (pageNumber > numPages) {
                    setupExtraCanvas(el, pageIndex);
                  }
                }}
              />
              {confirmation[pageIndex] ? (
                <Box className="fileRemoveIconWrapper">
                  <Button onClick={() => onPageRemove(pageIndex)}>
                    <CheckIcon size={24} weight="bold" />
                  </Button>
                  <Button
                    className="fileRemoveIcon"
                    onClick={() =>
                      setConfirmation((prev) => ({
                        ...prev,
                        [pageIndex]: false,
                      }))
                    }>
                    <XIcon size={24} weight="bold" />
                  </Button>
                </Box>
              ) : (
                <Box className="fileRemoveIconWrapper">
                  <Button
                    onClick={() =>
                      setConfirmation((prev) => ({
                        ...prev,
                        [pageIndex]: true,
                      }))
                    }>
                    <FileXIcon size={24} weight="bold" />
                  </Button>
                </Box>
              )}
            </Box>
          );
        })}
      </Document>
    </Box>
  );
}
